<template>
  <div>
    <h2 class="text-h6">Schedule</h2>
    <h3 class="h4 subtitle mb-4 text-muted">Set up automated emails to your clients.</h3>

    <c-row class="mt-5">
      <c-col sm="12" md="4">
        <c-row>
          <c-col sm="12">
            <c-select
              :value="frequencyOptions"
              label="Every"
              :options="frequencyOptions"
              @update:value="
                e => {
                  this.selectedFrequency = e;
                  changeFrequency();
                }
              "
            />
          </c-col>
          <template v-if="['week', 'fortnight'].indexOf(selectedFrequency) !== -1">
            <c-col sm="12">
              <c-select
                :value="selectedDayOption"
                label="On a"
                :options="dayList"
                @update:value="
                  e => {
                    this.selectedDayOption = e;
                    checkAllowSave();
                  }
                "
              />
            </c-col>
          </template>
          <template v-if="selectedFrequency === 'month'">
            <c-col sm="12">
              <c-select
                :value="selectedMonthOption"
                label="On the"
                :options="monthOptions"
                @update:value="
                  e => {
                    this.selectedMonthOption = e;
                    checkAndResetDay();
                  }
                "
              />
            </c-col>
            <c-col sm="12">
              <c-select
                :value="selectedDayOption"
                :options="dayList"
                @update:value="
                  e => {
                    this.selectedDayOption = e;
                    checkAllowSave();
                  }
                "
              />
            </c-col>
          </template>
          <c-col v-if="selectedDayOption" sm="12">
            <c-select
              label="Between"
              :value="this.schedule.hour"
              :options="timeOptions"
              @update:value="
                e => {
                  this.schedule.hour = e;
                  checkAllowSave();
                }
              "
            />
          </c-col>
        </c-row>
      </c-col>
      <c-col sm="12" md="8">
        <div v-if="selectedFrequency && selectedDayOption" class="preview-text px-5 mt-4">
          <p v-if="selectedFrequency === 'week'" class="h4 subtitle mb-3">
            An email will be sent to
            <span>{{ client.name }}</span>
            , once a
            <span>{{ selectedFrequency }}</span>
            on a
            <span>{{ getDayName(selectedDayOption) }}</span>
            between
            <span>{{ getHourName(schedule.hour) }}</span>
            .
          </p>
          <p v-if="selectedFrequency === 'fortnight'" class="h4 subtitle mb-3">
            An email will be sent to
            <span>{{ client.name }}</span>
            , once every
            <span>14 days</span>
            on a
            <span>{{ getDayName(selectedDayOption) }}</span>
            between
            <span>{{ getHourName(schedule.hour) }}</span>
            .
          </p>
          <p v-if="selectedFrequency === 'month'" class="h4 subtitle mb-3">
            An email will be sent to
            <span>{{ client.name }}</span>
            , once a
            <span>{{ selectedFrequency }}</span>
            on the
            <span>{{ getMonthOptionName(selectedMonthOption) }}</span>
            <span>{{ getDayName(selectedDayOption) }}</span>
            of the month, between
            <span>{{ getHourName(schedule.hour) }}</span>
            .
          </p>
          <p v-if="schedule.startDate" class="h4 subtitle mb-3">
            This schedule will begin on or after the
            <span>{{ schedule.startDate | dateFormatNoTime }}</span>
            .
          </p>
          <template v-if="schedule.startDate">
            <p v-if="schedule.endDate" class="h4 subtitle mb-3">
              This schedule will end on
              <span>{{ schedule.endDate | dateFormatNoTime }}</span>
              .
            </p>
            <p v-if="!schedule.endDate" class="h4 subtitle mb-3">
              This schedule has
              <span>no</span>
              end date.
            </p>
          </template>
        </div>
        <div v-if="showDebug">
          <pre>{{ selectedDayOption }}</pre>
          <pre>{{ contextDate }}</pre>
        </div>
      </c-col>
    </c-row>

    <c-row v-if="selectedFrequency && selectedDayOption">
      <c-col sm="12" md="4">
        <label>Start date</label>
        <v-date-picker v-model="schedule.startDate" label="Start date" mode="single" is-inline />
      </c-col>
      <c-col sm="12" md="4">
        <label>End date</label>
        <v-date-picker v-if="schedule.startDate" v-model="schedule.endDate" mode="single" is-inline />
      </c-col>
    </c-row>

    <c-row class="my-3">
      <c-col sm="1">
        <label class="label my-1" for="enabled">
          <span><strong>Enabled</strong></span>
        </label>
      </c-col>
      <c-col>
        <c-switch
          id="enabled"
          class="float-left"
          color="success"
          :checked="enabled"
          shape="pill"
          @update:checked="toggleEnabled"
        />
      </c-col>
    </c-row>

    <c-button-group>
      <c-button :disabled="!enableSave" color="primary" @click="save">
        <c-spinner v-if="isSaving" color="success" size="sm" />
        <span v-else>Update</span>
      </c-button>
      <c-button v-show="showCancelButton" color="secondary" @click="cancel">Cancel</c-button>
    </c-button-group>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { DatePicker } from 'v-calendar';
import { buildEndpoint } from '@/libs/buildEndPoint';

export default {
  components: {
    'v-date-picker': DatePicker,
  },

  props: {
    client: Object,
  },

  data() {
    return {
      error: null,
      isSaving: false,
      showDebug: false,
      showCancelButton: false,
      enableSave: false,
      enabled: false,
      dataLoaded: false,
      today: moment().format('Y-MM-DD'),
      schedule: {
        startDate: null,
        endDate: null,
        time: '',
        hour: 9,
      },
      frequencyOptions: [
        {
          value: 'week',
          label: '1 Week',
        },
        {
          value: 'fortnight',
          label: '2 Weeks',
        },
        {
          value: 'month',
          label: 'Month',
        },
      ],
      selectedFrequency: 'week',
      dayList: [
        {
          value: '0',
          label: 'Choose a day',
        },
        {
          value: '1',
          label: 'Monday',
        },
        {
          value: '2',
          label: 'Tuesday',
        },
        {
          value: '3',
          label: 'Wednesday',
        },
        {
          value: '4',
          label: 'Thursday',
        },
        {
          value: '5',
          label: 'Friday',
        },
        {
          value: '6',
          label: 'Saturday',
        },
        {
          value: '0',
          label: 'Sunday',
        },
      ],
      selectedDayOption: 0,
      monthOptions: [
        {
          value: '0',
          label: 'Choose an option',
        },
        {
          value: 'first',
          label: 'First',
        },
        {
          value: 'last',
          label: 'Last',
        },
      ],
      timeOptions: [
        {
          value: '0',
          label: '0:00 AM and 1:00 AM',
        },
        {
          value: '1',
          label: '1:00 AM and 2:00 AM',
        },
        {
          value: '2',
          label: '2:00 AM and 3:00 AM',
        },
        {
          value: '3',
          label: '3:00 AM and 4:00 AM',
        },
        {
          value: '4',
          label: '4:00 AM and 5:00 AM',
        },
        {
          value: '5',
          label: '5:00 AM and 6:00 AM',
        },
        {
          value: '6',
          label: '6:00 AM and 7:00 AM',
        },
        {
          value: '7',
          label: '7:00 AM and 8:00 AM',
        },
        {
          value: '8',
          label: '8:00 AM and 9:00 AM',
        },
        {
          value: '9',
          label: '9:00 AM and 10:00 AM',
        },
        {
          value: '10',
          label: '10:00 AM and 11:00 AM',
        },
        {
          value: '11',
          label: '11:00 AM and 12:00 PM',
        },
        {
          value: '12',
          label: '12:00 PM and 1:00 PM',
        },
        {
          value: '13',
          label: '1:00 PM and 2:00 PM',
        },
        {
          value: '14',
          label: '2:00 PM and 3:00 PM',
        },
        {
          value: '15',
          label: '3:00 PM and 4:00 PM',
        },
        {
          value: '16',
          label: '4:00 PM and 5:00 PM',
        },
        {
          value: '17',
          label: '5:00 PM and 6:00 PM',
        },
        {
          value: '18',
          label: '6:00 PM and 7:00 PM',
        },
        {
          value: '19',
          label: '7:00 PM and 8:00 PM',
        },
        {
          value: '20',
          label: '8:00 PM and 9:00 PM',
        },
        {
          value: '21',
          label: '9:00 PM and 10:00 PM',
        },
        {
          value: '22',
          label: '10:00 PM and 11:00 PM',
        },
        {
          value: '23',
          label: '11:00 PM and 12:00 PM',
        },
        {
          value: '24',
          label: '12:00 PM and 1:00 AM',
        },
      ],
      selectedMonthOption: 0,
      selectedTimeOption: 9,
      contextDate: {},
      datePickerMask: {
        input: 'YYYY-MM-DD',
      },
    };
  },
  computed: {},
  created() {
    this.setData();
  },

  methods: {
    checkAllowSave() {
      this.showCancelButton = true;
      this.enableSave = this.haveRequiredValues();
    },
    resetButtonState() {
      this.showCancelButton = false;
      this.enableSave = this.haveRequiredValues();
    },
    changeFrequency() {
      this.selectedDayOption = 0;
      this.selectedMonthOption = 0;
      this.checkAllowSave();
    },
    cancel() {
      this.setData();
      this.resetButtonState();
    },
    setData() {
      if (this.client) {
        const data = _.get(this.client, 'config.email.schedule');
        if (data) {
          this.selectedFrequency = data.frequency;
          if (data.startDate) {
            const startDate = moment(data.startDate);
            this.schedule.startDate = new Date(startDate.year(), startDate.month(), startDate.day());
          }
          if (data.endDate) {
            const endDate = moment(data.endDate);
            this.schedule.endDate = new Date(endDate.year(), endDate.month(), endDate.day());
          }
          this.schedule.time = data.time;
          this.schedule.hour = String(data.hour);
          this.enabled = data.enabled;
          this.selectedDayOption = String(data.dayNumber);
          this.selectedMonthOption = data.monthOption;
        }
      }
      this.dataLoaded = true;
    },
    setSavedData() {
      _.set(this.client, 'config.email.schedule', {
        startDate: this.schedule.startDate,
        endDate: this.schedule.endDate,
        time: this.time,
        hour: this.schedule.hour,
        enabled: this.enabled,
        dayNumber: this.selectedDayOption,
        monthOption: this.selectedMonthOption,
      });
    },
    clearEndDate() {
      this.schedule.endDate = null;
    },
    getDayName(code) {
      if (code) {
        return _.find(this.dayList, { value: code }).label;
      }
    },
    getHourName(code) {
      if (code) {
        return _.find(this.timeOptions, { value: String(code) }).label;
      }
    },
    getMonthOptionName(code) {
      if (code) {
        return _.find(this.monthOptions, { value: code }).label;
      }
    },
    getDateContext() {
      const date = moment(this.schedule.startDate);
      this.contextDate = {
        moment: date,
        daysInMonth: date.daysInMonth(),
        dayNumberInWeek: date.weekday(),
        dayNumberInMonth: date.date(),
        dayName: date.format('dddd'),
        weekInMonth: this.getMonthWeek(this.schedule.startDate),
        lastInMonth: this.checkLastDayInMonth(date.date(), date.daysInMonth()),
        firstInMonth: this.checkFirstDayInMonth(date.date()),
      };
    },
    checkFirstDayInMonth(dayNumber) {
      // If day in month is less than 7, true.
      return dayNumber <= 7;
    },
    checkLastDayInMonth(dayNumber, daysInMonth) {
      // If day in month is less than 7, true.
      return dayNumber > daysInMonth - 7;
    },
    getMonthWeek(date) {
      const newDate = new Date(date);
      const firstDay = new Date(newDate.getFullYear(), newDate.getMonth(), 1).getDay();
      return Math.ceil((newDate.getDate() + firstDay) / 7);
    },
    checkAndResetDay() {
      if (this.selectedMonthOption === '0') {
        this.selectedDayOption = 0;
      }
      this.checkAllowSave();
    },
    haveRequiredValues() {
      if (
        this.selectedFrequency
        && this.schedule.startDate
        && this.schedule.hour
        && this.selectedDayOption !== '0'
        && this.selectedDayOption !== 0
      ) {
        if (this.selectedFrequency === 'month') {
          return this.selectedMonthOption !== '0' && this.selectedMonthOption !== 0;
        }
        return true;
      }
      return false;
    },
    toggleEnabled(enabled) {
      this.enabled = enabled;
      this.checkAllowSave();
    },
    save() {
      const data = {
        clientId: this.client._id,
        frequency: this.selectedFrequency,
        dayNumber: this.selectedDayOption,
        monthOption: this.selectedMonthOption,
        enabled: this.enabled,
        time: this.schedule.time,
        hour: this.schedule.hour,
      };

      if (this.schedule.startDate) {
        data.startDate = moment(this.schedule.startDate).format('Y-MM-DD');
      }
      if (this.schedule.endDate) {
        data.endDate = moment(this.schedule.endDate).format('Y-MM-DD');
      }

      this.isSaving = true;
      this.$http
        .post(buildEndpoint('clients/email/update-schedule'), data)
        .then((response) => {
          this.isSaving = false;
          if (response.data.success) {
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message: 'Email schedule saved',
            });
            this.setSavedData();
            this.resetButtonState();
          } else {
            this.error = 'Error saving email schedule';
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'Error saving email schedule',
            });
          }
        })
        .catch((error) => {
          this.isSaving = false;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped="">
//.toggle {
//  margin-top: 7px;
//}
//.schedule-form {
//  .control {
//    > .control-label {
//      max-width: 150px !important;
//    }
//    > .control {
//      max-width: 350px !important;
//    }
//  }
//}
//.preview-area {
//  margin: 0 40px;
//  .preview-text {
//    p {
//      &>span {
//        font-weight: 500;
//      }
//    }
//  }
//}
</style>
<style lang="scss">
//.end-date {
//  .flatpickr-input {
//    width: 317px !important;
//  }
//}
</style>
